import { useCallback, useEffect, useRef, useState } from 'react'

import Brainfish from '@brainfish-ai/web-widget'

import { getColorTokenValue } from '@buffer-mono/popcorn'

import { env } from '~publish/env'
import { logError } from '~publish/legacy/utils/logError'

import { trackBrainfishClosed, trackBrainfishOpened } from './tracking'
import type { HelpWidgetAccount } from './types'

function toHex(value: number): string {
  return Math.round(value).toString(16).padStart(2, '0')
}

function getTokenHex(tokenName: string): string | undefined {
  const rgb = getColorTokenValue(tokenName)
  if (!rgb) {
    return undefined
  }

  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`
}

export function useBrainfishWidget(account: HelpWidgetAccount | null): {
  open: () => void
  close: () => void
  isReady: boolean
  isOpen: boolean
} {
  const isInitialized = useRef(false)
  const isIdentified = useRef(false)
  const [isReady, setIsReady] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (isInitialized.current) return
    isInitialized.current = true

    const initBrainfish = async (): Promise<void> => {
      // Let the theme tokens land on the DOM before getTokenHex reads them
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)),
      )

      try {
        await Brainfish.Widgets.init({
          widgetKey: env.VITE_BRAINFISH_WIDGET_KEY,
          overrides: {
            theme: {
              inputBorderColor: getTokenHex('color-border-subtle'),
              textBaseColor: getTokenHex('color-text-inverted'),
              inputButtonColor: getTokenHex('color-text-on-light'),
              inputButtonBgColor: getTokenHex('global-color-green-900'),
            },
          },
        })

        // Both panels open in the same corner, so the Beacon escalation from
        // Brainfish must replace the Brainfish panel, not stack on top of it.
        window.Beacon?.('on', 'open', () => Brainfish.Widgets.close())

        setIsReady(true)
      } catch (error) {
        logError(error instanceof Error ? error : new Error(String(error)), {
          context: 'HelpWidget',
        })
      }
    }

    void initBrainfish()
  }, [])

  useEffect(() => {
    if (
      isIdentified.current ||
      !isReady ||
      !account?.id ||
      account.isImpersonation
    ) {
      return
    }
    isIdentified.current = true

    window.BrainfishAnalytics?.('identify', {
      userId: account.id,
      email: account.email,
      properties: {
        signUpDate: account.createdAt,
        name: account.name,
        channelCountAcrossAllOrgs: account.channels?.length,
        organizationCount: account.organizations?.length,
        currentOrganizationId: account.currentOrganization?.id,
        hasVerifiedEmail: account.hasVerifiedEmail,
      },
    })
  }, [account, isReady])

  useEffect(() => {
    const handleOpen = (): void => setIsOpen(true)
    const handleClose = (): void => setIsOpen(false)

    window.addEventListener('onBrainfishWidgetOpen', handleOpen)
    window.addEventListener('onBrainfishWidgetClose', handleClose)

    return (): void => {
      window.removeEventListener('onBrainfishWidgetOpen', handleOpen)
      window.removeEventListener('onBrainfishWidgetClose', handleClose)
    }
  }, [])

  const open = useCallback((): void => {
    Brainfish.Widgets.open()
    trackBrainfishOpened(account)
    setIsOpen(true)
  }, [account])

  const close = useCallback((): void => {
    Brainfish.Widgets.close()
    trackBrainfishClosed(account)
    setIsOpen(false)
  }, [account])

  return { open, close, isReady, isOpen }
}
